import { useState } from 'react';
import { logError } from '../../lib/logger';
import { ArrowUp, ArrowDown, Trash2, Loader2, Link2, Plus } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const emptyDraft = { url: '', title: '', description: '', image_url: '', source_name: '' };

export const AdminManualStoryBuilder = () => {
  const [headline, setHeadline] = useState('');
  const [urlInput, setUrlInput] = useState('');
  const [draft, setDraft] = useState(emptyDraft);
  const [fetching, setFetching] = useState(false);
  const [articles, setArticles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');

  const handleFetchPreview = async () => {
    if (!urlInput.trim()) return;
    setFetching(true);
    setStatusMsg('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/scrape-preview`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ url: urlInput.trim() }),
      });
      if (res.ok) {
        const data = await res.json();
        setDraft({
          url: data.url,
          title: data.title || '',
          description: data.description || '',
          image_url: data.image_url || '',
          source_name: data.source_name || '',
        });
      } else {
        setDraft({ ...emptyDraft, url: urlInput.trim() });
        setStatusMsg("Couldn't auto-fetch that link - fill in the details manually below.");
      }
    } catch (e) {
      logError('AdminManualStoryBuilder:fetchPreview', e);
      setDraft({ ...emptyDraft, url: urlInput.trim() });
      setStatusMsg("Couldn't auto-fetch that link - fill in the details manually below.");
    } finally {
      setFetching(false);
    }
  };

  const handleAddToSpectrum = () => {
    if (!draft.url.trim() || !draft.title.trim()) {
      setStatusMsg('A URL and title are required before adding to the spectrum.');
      return;
    }
    setArticles((prev) => [...prev, draft]);
    setDraft(emptyDraft);
    setUrlInput('');
    setStatusMsg('');
  };

  const moveArticle = (index, dir) => {
    setArticles((prev) => {
      const next = [...prev];
      const target = index + dir;
      if (target < 0 || target >= next.length) return prev;
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  };

  const removeArticle = (index) => {
    setArticles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleCreateStory = async () => {
    if (!headline.trim() || articles.length < 2) {
      setStatusMsg('Add a headline and at least 2 articles (in stance order) before creating the story.');
      return;
    }
    setSubmitting(true);
    setStatusMsg('');
    try {
      const res = await fetch(`${BACKEND_URL}/api/admin/stories/manual`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ headline: headline.trim(), articles }),
      });
      if (res.ok) {
        setStatusMsg('Story created and published to today\'s homepage feed.');
        setHeadline('');
        setArticles([]);
      } else {
        const err = await res.json();
        setStatusMsg(err.detail || 'Failed to create story.');
      }
    } catch (e) {
      logError('AdminManualStoryBuilder:create', e);
      setStatusMsg('Failed to create story.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-manual-story-builder">
      <div>
        <label className="text-xs font-medium text-gray-600 mb-1 block">Story headline</label>
        <input
          value={headline}
          onChange={(e) => setHeadline(e.target.value)}
          placeholder="e.g. Parliament passes new data bill"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          data-testid="admin-headline-input"
        />
      </div>

      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <label className="text-xs font-medium text-gray-600 mb-1 block">Paste an article link</label>
        <div className="flex gap-2">
          <input
            value={urlInput}
            onChange={(e) => setUrlInput(e.target.value)}
            placeholder="https://..."
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            data-testid="admin-scrape-url-input"
          />
          <button
            onClick={handleFetchPreview}
            disabled={fetching || !urlInput.trim()}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-gray-900 text-white text-sm font-medium disabled:opacity-50"
            data-testid="admin-fetch-preview-btn"
          >
            {fetching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Link2 className="w-4 h-4" />}
            Fetch
          </button>
        </div>

        {(draft.url || draft.title) && (
          <div className="mt-3 space-y-2" data-testid="admin-draft-preview">
            <input
              value={draft.title}
              onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              placeholder="Title"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="admin-draft-title-input"
            />
            <textarea
              value={draft.description}
              onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
              placeholder="Description (optional)"
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm"
              data-testid="admin-draft-description-input"
            />
            <div className="flex gap-2">
              <input
                value={draft.source_name}
                onChange={(e) => setDraft((d) => ({ ...d, source_name: e.target.value }))}
                placeholder="Source name"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                data-testid="admin-draft-source-input"
              />
              <input
                value={draft.image_url}
                onChange={(e) => setDraft((d) => ({ ...d, image_url: e.target.value }))}
                placeholder="Image URL (optional)"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                data-testid="admin-draft-image-input"
              />
            </div>
            <button
              onClick={handleAddToSpectrum}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-blue-600 text-white text-xs font-medium hover:bg-blue-700"
              data-testid="admin-add-to-spectrum-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              Add to spectrum
            </button>
          </div>
        )}
      </div>

      {articles.length > 0 && (
        <div>
          <p className="text-xs font-medium text-gray-600 mb-2">Stance order (left → right)</p>
          <div className="space-y-2">
            {articles.map((a, index) => (
              <div key={index} className="flex items-center gap-3 p-3 border border-gray-200 rounded-lg" data-testid={`admin-draft-article-${index}`}>
                <span className="text-xs font-semibold text-gray-400 w-5">{index + 1}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{a.title}</p>
                  <p className="text-xs text-gray-500">{a.source_name || 'Manual'}</p>
                </div>
                <button onClick={() => moveArticle(index, -1)} disabled={index === 0} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" data-testid={`admin-move-up-${index}`}>
                  <ArrowUp className="w-4 h-4" />
                </button>
                <button onClick={() => moveArticle(index, 1)} disabled={index === articles.length - 1} className="p-1.5 rounded hover:bg-gray-100 disabled:opacity-30" data-testid={`admin-move-down-${index}`}>
                  <ArrowDown className="w-4 h-4" />
                </button>
                <button onClick={() => removeArticle(index)} className="p-1.5 rounded hover:bg-red-50 text-red-600" data-testid={`admin-remove-draft-${index}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}

      {statusMsg && <p className="text-sm text-amber-700" data-testid="admin-manual-status-msg">{statusMsg}</p>}

      <button
        onClick={handleCreateStory}
        disabled={submitting || articles.length < 2 || !headline.trim()}
        className="w-full py-2.5 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-40 hover:bg-emerald-700"
        data-testid="admin-create-story-btn"
      >
        {submitting ? 'Creating...' : `Create story (${articles.length} source${articles.length === 1 ? '' : 's'})`}
      </button>
    </div>
  );
};
